import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type { InsightsProductUsageDailyPoint } from "@shared-core/data/api-types";
import { shortDayLabel } from "@shared-core/domain/insights-product-usage";
import { niceAxisMax } from "@shared-core/domain/insights-monthly-revenue";

// Activity by day across the window (spec §8 point 4) — total page views
// per calendar day, narrowed to the staff filter selected above. Weekend
// days read near-zero, which is the expected rhythm, not missing data
// (same convention as the Daily revenue tab).

const BAR_COLOR = "var(--color-chart-1)";

export interface ProductUsageDailyChartProps {
  daily: InsightsProductUsageDailyPoint[];
}

export function ProductUsageDailyChart({ daily }: ProductUsageDailyChartProps) {
  if (daily.length === 0) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No daily activity for this window yet.
      </div>
    );
  }

  const data = daily.map((d) => ({ ...d, label: shortDayLabel(d.day) }));
  const valueMax = niceAxisMax(Math.max(0, ...data.map((d) => d.page_views)));

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-foreground text-base font-semibold">
          Activity by day
        </h2>
        <p className="text-muted-foreground text-sm">
          Page views per day, for the selected filter · {data.length}{" "}
          {data.length === 1 ? "day" : "days"}
        </p>
      </div>

      <div className="border-border bg-surface rounded-lg border p-4">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart
            data={data}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={{ stroke: "var(--color-border)" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis
              domain={[0, valueMax]}
              allowDataOverflow
              tickCount={5}
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip
              cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
              content={DailyTooltip as never}
            />
            <Bar
              dataKey="page_views"
              name="Page views"
              fill={BAR_COLOR}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
              fillOpacity={0.95}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DailyTooltip(
  props: TooltipContentProps<number, string> & {
    payload?: { payload: InsightsProductUsageDailyPoint & { label: string } }[];
  },
) {
  if (!props.active || !props.payload || props.payload.length === 0) {
    return null;
  }
  const datum = props.payload[0]?.payload;
  if (!datum) return null;
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-1 font-semibold">{datum.label}</div>
      <div className="tabular-nums">{datum.page_views} page views</div>
    </div>
  );
}
